import React, { useEffect, useState } from "react";
import { Users, LogIn, Loader2, Clock, CheckCircle2, AlertTriangle, XCircle, ClipboardList, Trophy, RefreshCw, Lock, LockOpen } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import api, { fmtTime, MONTHS, apiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DisbursementPanel from "@/components/admin/DisbursementPanel";

const predColor = (p) => ({
  "Disiplin Champion": "bg-violet-100 text-violet-700",
  "Sangat Disiplin": "bg-emerald-100 text-emerald-700",
  "Disiplin": "bg-sky-100 text-[#3368A0]",
  "Cukup Disiplin": "bg-amber-100 text-amber-700",
  "Perlu Improvement": "bg-orange-100 text-orange-700",
  "Perlu Perhatian": "bg-rose-100 text-rose-700",
}[p] || "bg-slate-100 text-slate-600");

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="text-2xl font-extrabold text-slate-800 leading-none">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  </div>
);

export default function DashboardTab() {
  const now = new Date();
  const [data, setData] = useState(null);
  const [chart, setChart] = useState(null);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year] = useState(now.getFullYear());
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [sumMonth, setSumMonth] = useState(now.getMonth() + 1);
  const [board, setBoard] = useState(null);
  const [discMonth, setDiscMonth] = useState(now.getMonth() + 1);
  const [recalcing, setRecalcing] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [discDetail, setDiscDetail] = useState(null);
  const [detailUserId, setDetailUserId] = useState(null);
  const [adjPoints, setAdjPoints] = useState("");
  const [adjReason, setAdjReason] = useState("");

  const loadBoard = (m) => api.get(`/discipline/leaderboard?month=${m}&year=${year}`).then((r) => setBoard(r.data));

  useEffect(() => {
    api.get("/dashboard/today").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadBoard(discMonth); }, [discMonth, year]); // eslint-disable-line
  useEffect(() => {
    api.get(`/dashboard/chart?month=${month}&year=${year}`).then((r) => setChart(r.data));
  }, [month, year]);
  useEffect(() => {
    api.get(`/dashboard/payroll-summary?month=${sumMonth}&year=${year}`).then((r) => setSummary(r.data));
  }, [sumMonth, year]);

  const recalc = async () => {
    setRecalcing(true);
    try { await api.post(`/discipline/admin/recalculate?month=${discMonth}&year=${year}`); await loadBoard(discMonth); toast.success("Skor disiplin dihitung ulang."); }
    catch (e) { toast.error(apiError(e)); } finally { setRecalcing(false); }
  };
  const togglePeriod = async (action) => {
    try { await api.post(`/discipline/admin/period/${action}`, { month: discMonth, year }); await loadBoard(discMonth); toast.success(action === "close" ? "Periode ditutup." : "Periode dibuka."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openDetail = async (uid) => {
    setDetailUserId(uid);
    try { const { data } = await api.get(`/discipline/admin/${uid}?month=${discMonth}&year=${year}`); setDiscDetail(data); setDetailOpen(true); setAdjPoints(""); setAdjReason(""); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submitAdjust = async () => {
    const pts = Number(adjPoints);
    if (!pts) { toast.error("Masukkan poin (boleh negatif)."); return; }
    try {
      await api.post("/discipline/admin/adjust", { employee_id: detailUserId, month: discMonth, year, points: pts, reason: adjReason });
      toast.success("Penyesuaian tersimpan.");
      await openDetail(detailUserId); await loadBoard(discMonth);
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadDisbursement = async () => {
    const res = await api.get(`/dashboard/payroll-export?month=${sumMonth}&year=${year}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "disbursement_gaji.xlsx"; a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#3368A0]" /></div>;

  const s = data.stats;
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-slate-800">Statistik Hari Ini</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-stats">
        <StatCard label="Terjadwal" value={s.terjadwal} icon={Users} color="bg-slate-100 text-slate-600" />
        <StatCard label="Sudah Check-In" value={s.sudah_checkin} icon={LogIn} color="bg-sky-100 text-[#3368A0]" />
        <StatCard label="Sedang Bekerja" value={s.sedang_bekerja} icon={Clock} color="bg-sky-100 text-[#3368A0]" />
        <StatCard label="Sudah Check-Out" value={s.sudah_checkout} icon={CheckCircle2} color="bg-emerald-100 text-emerald-600" />
        <StatCard label="Terlambat" value={s.terlambat} icon={AlertTriangle} color="bg-amber-100 text-amber-600" />
        <StatCard label="Pulang Lebih Awal" value={s.pulang_lebih_awal || 0} icon={Clock} color="bg-orange-100 text-orange-600" />
        <StatCard label="Belum Absen" value={s.belum_absen} icon={XCircle} color="bg-slate-100 text-slate-600" />
        <StatCard label="Perlu Review" value={s.perlu_review} icon={ClipboardList} color="bg-purple-100 text-purple-600" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-semibold text-slate-800">Grafik Kehadiran Bulanan</h3>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-40" data-testid="chart-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m} {year}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart?.data || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip />
              <Legend />
              <Bar dataKey="hadir" name="Hadir" fill="#3368A0" radius={[4, 4, 0, 0]} />
              <Bar dataKey="terlambat" name="Terlambat" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Bar dataKey="pulang_lebih_awal" name="Pulang Lebih Awal" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {summary && (
        <DisbursementPanel
          summary={summary}
          sumMonth={sumMonth}
          year={year}
          months={MONTHS}
          onMonthChange={setSumMonth}
          onDownload={downloadDisbursement}
        />
      )}

      {board && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="discipline-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /> Skoring Kedisiplinan Karyawan</h3>
              <p className="text-xs text-slate-500">Periode {MONTHS[board.month - 1]} {board.year} · Status: <span className="font-semibold">{board.period_status}</span> · Skor 0–130</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={String(discMonth)} onValueChange={(v) => setDiscMonth(Number(v))}>
                <SelectTrigger className="w-36 h-9" data-testid="discipline-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={recalc} disabled={recalcing} data-testid="discipline-recalc-button"><RefreshCw className={`h-4 w-4 mr-1 ${recalcing ? "animate-spin" : ""}`} /> Hitung Ulang</Button>
              {board.period_status === "CLOSED" ? (
                <Button variant="outline" size="sm" onClick={() => togglePeriod("reopen")} data-testid="discipline-reopen-button"><LockOpen className="h-4 w-4 mr-1" /> Buka Periode</Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => togglePeriod("close")} data-testid="discipline-close-button"><Lock className="h-4 w-4 mr-1" /> Tutup Periode</Button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="discipline-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2.5">#</th>
                  <th className="text-left px-4 py-2.5">Nama</th>
                  <th className="text-left px-4 py-2.5">Jabatan</th>
                  <th className="text-center px-4 py-2.5">Bonus</th>
                  <th className="text-center px-4 py-2.5">Penalti</th>
                  <th className="text-center px-4 py-2.5">Streak</th>
                  <th className="text-right px-4 py-2.5">Skor</th>
                  <th className="text-left px-4 py-2.5">Predikat</th>
                  <th className="text-center px-4 py-2.5">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {board.rows.length === 0 && (<tr><td colSpan={9} className="text-center text-slate-400 py-6">Belum ada data.</td></tr>)}
                {board.rows.map((r) => (
                  <tr key={r.user_id} className="border-t border-slate-100" data-testid={`discipline-row-${r.rank}`}>
                    <td className="px-4 py-2.5 font-bold text-slate-400">{r.rank}</td>
                    <td className="px-4 py-2.5 font-medium text-slate-700">{r.user_name}{r.has_absence_violation && <span title="Ada mangkir" className="ml-1 text-rose-500">●</span>}</td>
                    <td className="px-4 py-2.5 text-slate-500">{r.position}</td>
                    <td className="px-4 py-2.5 text-center text-emerald-600">+{r.total_bonus}</td>
                    <td className="px-4 py-2.5 text-center text-rose-600">-{r.total_penalty}</td>
                    <td className="px-4 py-2.5 text-center text-orange-600">{r.current_streak}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-[#3368A0]">{r.final_score}<span className="text-xs text-slate-400">/{r.max_score}</span></td>
                    <td className="px-4 py-2.5"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${predColor(r.predicate)}`}>{r.predicate}</span></td>
                    <td className="px-4 py-2.5 text-center"><button onClick={() => openDetail(r.user_id)} className="text-[#3368A0] text-xs font-semibold hover:underline" data-testid={`discipline-detail-${r.user_id}`}>Detail</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <h3 className="text-lg font-semibold text-slate-800 p-5 pb-3">Kehadiran Hari Ini</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dashboard-today-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-3">Nama</th>
                <th className="text-left px-5 py-3">Outlet</th>
                <th className="text-left px-5 py-3">Shift</th>
                <th className="text-left px-5 py-3">Check-In</th>
                <th className="text-left px-5 py-3">Check-Out</th>
                <th className="text-left px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-slate-400 py-8">Belum ada jadwal hari ini.</td></tr>
              )}
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-medium text-slate-700">{r.user_name}</td>
                  <td className="px-5 py-3 text-slate-500">{r.outlet_name}</td>
                  <td className="px-5 py-3 text-slate-500">{r.shift_name}</td>
                  <td className="px-5 py-3 font-mono text-slate-600">{fmtTime(r.check_in_time)}</td>
                  <td className="px-5 py-3 font-mono text-slate-600">{fmtTime(r.check_out_time)}</td>
                  <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="discipline-detail-dialog">
          <DialogHeader><DialogTitle>Rincian Disiplin — {discDetail?.user_name}</DialogTitle></DialogHeader>
          {discDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-400">Skor</p><p className="text-xl font-bold text-[#3368A0]">{discDetail.final_score}/{discDetail.max_score}</p></div>
                <div className="rounded-xl bg-emerald-50 p-3"><p className="text-xs text-slate-400">Bonus</p><p className="text-xl font-bold text-emerald-600">+{discDetail.total_bonus}</p></div>
                <div className="rounded-xl bg-rose-50 p-3"><p className="text-xs text-slate-400">Penalti</p><p className="text-xl font-bold text-rose-600">-{discDetail.total_penalty}</p></div>
              </div>
              <p className="text-sm flex items-center gap-2 flex-wrap">Predikat: <span className={`text-xs font-semibold px-2 py-1 rounded-full ${predColor(discDetail.predicate)}`}>{discDetail.predicate}</span>
                <span className="text-xs text-slate-400">· Streak {discDetail.current_streak} hari · Status {discDetail.period_status}</span>
                {discDetail.has_absence_violation && <span className="text-xs text-rose-600">⚠ Ada mangkir (predikat dibatasi)</span>}
              </p>
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-1">Event Poin ({(discDetail.stored_events || []).length})</p>
                <div className="border rounded-xl divide-y max-h-56 overflow-y-auto" data-testid="discipline-events-list">
                  {(discDetail.stored_events || []).length === 0 ? <p className="text-center text-slate-400 text-xs py-3">Belum ada event.</p> :
                    discDetail.stored_events.map((e) => (
                      <div key={e.id} className={`flex items-center justify-between px-3 py-2 text-xs ${e.is_reversed ? "opacity-40 line-through" : ""}`}>
                        <div className="min-w-0 pr-2"><p className="text-slate-600 truncate">{e.reason} <span className="text-slate-300">· {e.rule_type}</span></p><p className="text-slate-400">{e.event_date} · {e.source_type}{e.is_reversed ? " · dibatalkan" : ""}</p></div>
                        <span className={`font-bold shrink-0 ${e.points >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{e.points >= 0 ? "+" : ""}{e.points}</span>
                      </div>
                    ))}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                <p className="text-sm font-semibold text-slate-700">Penyesuaian Manual (Adjustment)</p>
                <p className="text-xs text-slate-400">Ditambahkan sebagai event baru (histori tidak dihapus). Gunakan nilai negatif untuk mengurangi poin.</p>
                <div className="flex gap-2 flex-wrap">
                  <Input type="number" placeholder="Poin (mis. 5 / -5)" value={adjPoints} onChange={(e) => setAdjPoints(e.target.value)} className="w-40" data-testid="adjust-points-input" />
                  <Input placeholder="Alasan" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} className="flex-1 min-w-[140px]" data-testid="adjust-reason-input" />
                  <Button onClick={submitAdjust} data-testid="adjust-submit-button">Simpan</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
